/* eslint-disable i18next/no-literal-string */
// src/features/softwareLicenses/pages/LicenseDetailsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Computer as ComputerIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarTodayIcon,
  Update as UpdateIcon,
  Router as RouterIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { format } from 'date-fns';

// Hooks
import { useSoftwareLicenses } from '../hooks/useSoftwareLicenses';
import { useDepartments } from '../../departments/hooks/useDepartment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { parseApiTimestamp } from '../../../utils/formatters/dateFormatter';
import PageHeader from '../../../components/common/PageHeader';

export default function LicenseDetailsPage() {
  const { licenseId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { t } = useTranslation(['common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // State
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [activating, setActivating] = useState(false);

  // API hooks
  const {
    currentLicense,
    loading,
    error,
    clearError,
    fetchLicenseById,
    activateLicense,
    deactivateLicense,
    deleteLicense,
  } = useSoftwareLicenses();

  const { departments, fetchAllDepartments } = useDepartments();

  // Load license details
  useEffect(() => {
    if (licenseId) {
      fetchLicenseById(licenseId);
    }
  }, [licenseId, fetchLicenseById]);

  // Load departments
  useEffect(() => {
    fetchAllDepartments({ include_deactivated: false });
  }, [fetchAllDepartments]);

  const handleBack = () => {
    navigateBack('/software-licenses');
  };

  const handleActivateClick = () => {
    setActivateDialogOpen(true);
  };

  const handleActivateConfirm = async () => {
    if (!selectedDepartment) return;

    setActivating(true);
    try {
      const result = await activateLicense(licenseId, selectedDepartment.department_id);
      if (result.success) {
        showSuccess(`License activated for ${selectedDepartment.name}`);
        setActivateDialogOpen(false);
        setSelectedDepartment(null);
        // Refresh license data
        await fetchLicenseById(licenseId);
      } else {
        showError(result.error || 'Failed to activate license');
      }
    } catch (error) {
      showError('Failed to activate license');
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivateClick = () => {
    showConfirmDialog({
      title: 'Deactivate License',
      message: 'Are you sure you want to deactivate this license? The software will no longer be able to run on this machine.',
      confirmText: 'Deactivate',
      cancelText: 'Cancel',
      severity: 'warning',
      onConfirm: async () => {
        try {
          const result = await deactivateLicense(licenseId);
          if (result.success) {
            showSuccess('License deactivated');
            await fetchLicenseById(licenseId);
          } else {
            showError(result.error || 'Failed to deactivate license');
          }
        } catch (error) {
          showError('Failed to deactivate license');
        }
      },
    });
  };

  const handleDeleteClick = () => {
    showConfirmDialog({
      title: 'Delete License',
      message: 'Are you sure you want to delete this license? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteLicense(licenseId);
          if (result.success) {
            showSuccess('License deleted');
            navigate('/software-licenses');
          } else {
            showError(result.error || 'Failed to delete license');
          }
        } catch (error) {
          showError('Failed to delete license');
        }
      },
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (!currentLicense) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Failed to load license details
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to Licenses
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={handleBack}
        title={'License Details'}
        crumb={currentLicense.department_name}
        subtitle={'View and manage software license'}
        actions={<>
          {/* Actions */}
          <Box display="flex" gap={1}>
            {!currentLicense.is_activated ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleActivateClick}
              >
                Activate License
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<BlockIcon />}
                onClick={handleDeactivateClick}
              >
                Deactivate
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          </Box>
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Main License Card */}
        <Card>
          <CardContent>
            {/* Status Badge */}
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Software License
                </Typography>
                <Chip
                  icon={currentLicense.is_activated ? <CheckCircleIcon /> : <BlockIcon />}
                  label={currentLicense.is_activated ? 'Activated' : 'Pending Activation'}
                  color={currentLicense.is_activated ? 'success' : 'warning'}
                  variant="filled"
                />
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Hardware ID */}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <ComputerIcon color="action" />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                      Hardware ID
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>
                      {currentLicense.hwid}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Software Package */}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <ComputerIcon color="action" />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                      Software Package
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {currentLicense.software_package_id}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Department */}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <BusinessIcon color="action" />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {currentLicense.department_name || 'Not Assigned'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* IP Address */}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <RouterIcon color="action" />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                      IP Address
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {currentLicense.ip_address}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Created Date */}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CalendarTodayIcon color="action" />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                      Created Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {format(parseApiTimestamp(currentLicense.created_at), 'PPpp')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Last Updated */}
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <UpdateIcon color="action" />
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {format(parseApiTimestamp(currentLicense.updated_at), 'PPpp')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Last Accessed */}
              {currentLicense.last_accessed_at && (
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <UpdateIcon color="action" />
                    <Box flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        Last Accessed
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {format(parseApiTimestamp(currentLicense.last_accessed_at), 'PPpp')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* License ID */}
              <Grid item xs={12}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    License ID
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                    {currentLicense.license_id}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Status Information */}
        {!currentLicense.is_activated ? (
          <Alert severity="info">
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Pending Activation
            </Typography>
            <Typography variant="body2">
              This license is pending activation. Click "Activate License" above to assign it to a department. 
              Once activated, the software will be able to run on this machine.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="success">
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              License Active
            </Typography>
            <Typography variant="body2">
              This license is currently active and assigned to the {currentLicense.department_name} department. 
              The software can run on this machine.
            </Typography>
          </Alert>
        )}
      </Stack>

      {/* Activate License Dialog */}
      <Dialog
        open={activateDialogOpen}
        onClose={() => !activating && setActivateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={activating}
      >
        <DialogTitle>
          Activate License
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Select a department to activate this license. Once activated, the software will be able to run on this machine.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Hardware ID:</strong> {currentLicense.hwid}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Software Package:</strong> {currentLicense.software_package_id}
            </Typography>
          </Box>

          <Autocomplete
            options={departments}
            getOptionLabel={(option) => option.name}
            value={selectedDepartment}
            onChange={(event, value) => setSelectedDepartment(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Department"
                placeholder="Select department"
                margin="normal"
                fullWidth
                required
              />
            )}
            disabled={activating}
            noOptionsText={departments.length === 0 ? "No departments available" : "No matching departments"}
          />

          {departments.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No departments are available. Please create a department first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setActivateDialogOpen(false)} disabled={activating}>
            Cancel
          </Button>
          <Button
            onClick={handleActivateConfirm}
            variant="contained"
            color="success"
            disabled={!selectedDepartment || activating}
            startIcon={activating ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {activating ? 'Activating...' : 'Activate License'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
}